import { coreDaoFactory } from '../common'
import {
  AdminCommentListParams,
  AdminCommentsListResponse,
  CommunityComment,
  CommunityCommentsResponse,
  CommunityPagination,
  CommunityRepliesResponse,
  CommunityReply,
  CreateCommentPayload,
  ReactionPayload,
  ReactionSummary,
  ReactionTypeDefinition,
  UpdateAdminCommentPayload,
} from '../types/community'
import { errorUtil } from '../util/error-util'
import { HttpResponse } from '../util/http-util'

const dao = coreDaoFactory({
  onError: async (error: Error) => {
    errorUtil.handleHttpError(error)
  },
})

const unwrap = <T>(response: HttpResponse<T>): T[] => {
  return response.data
}

export const communityDao = {
  async listComments(params: {
    entityType: string
    entityId: string
    page?: number
    pageSize?: number
    sortBy?: string
  }): Promise<CommunityCommentsResponse> {
    const result = await dao._http.get<HttpResponse<CommunityComment>>('/comments', {
      params,
    })
    const pagination = result.data.meta as CommunityPagination
    return {
      data: unwrap(result.data),
      pagination,
    }
  },

  async listReplies(params: { commentId: string; page?: number; pageSize?: number }): Promise<CommunityRepliesResponse> {
    const { commentId, page, pageSize } = params
    const result = await dao._http.get<HttpResponse<CommunityReply>>(`/comments/${commentId}/replies`, {
      params: { page, pageSize },
    })
    const pagination = result.data.meta as CommunityPagination
    return {
      data: unwrap(result.data),
      pagination,
    }
  },

  async createComment(payload: CreateCommentPayload): Promise<CommunityComment> {
    const result = await dao._http.post<HttpResponse<CommunityComment>>('/comments', payload)
    return unwrap(result.data)[0]
  },

  async deleteComment(commentId: string): Promise<void> {
    await dao._http.delete(`/comments/${commentId}`)
  },

  async getReactions(params: { entityType: string; entityId: string }): Promise<ReactionSummary> {
    const result = await dao._http.get<HttpResponse<ReactionSummary>>('/reactions', {
      params,
    })
    return unwrap(result.data)[0] ?? { counts: {}, hustlerReactionType: null }
  },

  async toggleReaction(payload: ReactionPayload): Promise<ReactionSummary> {
    const result = await dao._http.post<HttpResponse<ReactionSummary>>('/reactions', payload)
    return unwrap(result.data)[0]
  },

  async getReactionTypes(): Promise<ReactionTypeDefinition[]> {
    const result = await dao._http.get<HttpResponse<ReactionTypeDefinition>>('/reaction-types')
    return unwrap(result.data)
  },

  async adminListComments(params: AdminCommentListParams): Promise<AdminCommentsListResponse> {
    const result = await dao._http.get<any>('/admin/comments', { params })
    const meta = result.data.meta ?? {}
    return {
      data: result.data.data as CommunityComment[],
      limit: meta.limit ?? params.limit,
      offset: meta.offset ?? params.offset,
      total: meta.total ?? result.data.data?.length ?? 0,
    }
  },

  async adminGetComment(commentId: string): Promise<CommunityComment> {
    const result = await dao._http.get<HttpResponse<CommunityComment>>(`/admin/comments/${commentId}`)
    return unwrap(result.data)[0]
  },

  async adminUpdateComment(commentId: string, payload: UpdateAdminCommentPayload): Promise<CommunityComment> {
    const result = await dao._http.put<HttpResponse<CommunityComment>>(`/admin/comments/${commentId}`, payload)
    return unwrap(result.data)[0]
  },

  async adminDeleteComment(commentId: string): Promise<void> {
    await dao._http.delete(`/admin/comments/${commentId}`)
  },
}
